import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Lightbulb } from 'lucide-react';
import { devicesService } from '../../services/devices';
import type { BoardObservation } from '../../types/device';

interface CurrentBoardProblemProps {
  gymSlug: string;
  boardId: string;
}

/**
 * What is lit on the wall right now, above the list of what could be.
 *
 * A climber who picks a problem on the official Moonboard or Kilter app never
 * tells us: the LEDs come on and DivePeaks knows nothing about it. Except that
 * the device in front of the board sees the frame go past and reports it, so we
 * can name the problem — or, when it is nobody's problem, at least draw the
 * holds. This is that, one line, at the top of the board tab: "the problem on
 * this wall is X", and a tap to go and look at it.
 *
 * Nothing is shown when the board has nothing to show. That is most of the
 * time: an observation lasts 15 minutes on the server, the frames arrive at QoS
 * 0, and only the climbers whose app happened to list our proxy produce any at
 * all. A missing line is the normal state, never an error worth a word.
 *
 * Read on mount and again whenever the app comes back to the foreground, which
 * is the moment it changes: the climber lit the problem in the other app and
 * switched over to this one.
 */
export default function CurrentBoardProblem({ gymSlug, boardId }: CurrentBoardProblemProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [observation, setObservation] = useState<BoardObservation | null>(null);

  const refresh = useCallback(() => {
    let cancelled = false;

    devicesService
      .getBoardObservation(gymSlug, boardId)
      .then((current) => {
        if (!cancelled) setObservation(current);
      })
      // The service already answers null on failure; this is the belt to its
      // braces, because a wall we cannot ask about must not take the list down.
      .catch(() => {
        if (!cancelled) setObservation(null);
      });

    return () => {
      cancelled = true;
    };
  }, [gymSlug, boardId]);

  useEffect(() => refresh(), [refresh]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  if (!observation) return null;

  const climb = observation.climb;

  // A problem we know opens on its own page. One we do not opens the editor on
  // the holds that are lit — the wall drawn as it stands, and the offer to make
  // it a problem. The holds are fetched there rather than carried in the URL,
  // so the page survives being reloaded or shared.
  const open = () =>
    navigate(
      climb
        ? `/gyms/${gymSlug}/boards/${boardId}/climbs/${climb.slug}`
        : `/gyms/${gymSlug}/boards/${boardId}/climbs/new?observed=1`
    );

  const name = climb?.name?.trim() || t('climbs.board.current.unknown');

  return (
    <button
      type="button"
      onClick={open}
      data-testid="current-board-problem"
      className="mb-3 flex w-full items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-left text-sm transition-colors hover:bg-primary/10"
    >
      <Lightbulb className="size-4 shrink-0 text-primary" />
      <span className="text-muted-foreground">{t('climbs.board.current.label')}</span>
      <span className="min-w-0 flex-1 truncate font-medium">{name}</span>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
    </button>
  );
}
